// THE BROADCAST TOOL — HER SCREEN. Her order, 2026-08-11 (queue item 4).
//
// **THE COMPOSER IS ONE FORM WITH TWO CHANNELS** (her clause), and every decision it renders is read
// out of `src/broadcasts.js` rather than made here: the audiences, the composition law, the typed
// confirmation and the capacity verdict are all the SERVER'S answers, so a screen that disagrees
// with the door is impossible rather than unlikely (R28/R29 — and on this surface the cost of two
// answers is a send to the wrong list of people).
//
// **THE ONE THING THIS FILE DECIDES FOR ITSELF IS THE ORDER OF THE THREE STEPS**, and that order IS
// her safety clause: compose, then PREVIEW, then a typed confirmation. The Send control does not
// exist until a preview has come back from the server, so there is no path through this screen where
// something fires without her having read exactly what a member will see.
import { Fragment, useEffect, useRef, useState } from "react";
import { DeleteControl } from "./DeleteControl";
// HER BUTTON RULING — the picker's roster, derived from the house's own route map and funnel law.
import { BROADCAST_DESTINATIONS, DESTINATION_GROUPS, destinationForPath } from "../lib/broadcast-destinations";
import {
  isExternalDestination,
  BROADCAST_AUDIENCES,
  BROADCAST_BODY_MAX,
  BROADCAST_PUSH_BODY_MAX,
  BROADCAST_SUBJECT_MAX,
  EMAIL_BLOCK_KINDS,
  EMAIL_BLOCKS_MAX,
  EMAIL_PREHEADER_MAX,
  PREVIEW_LINK_NOTE,
  PREVIEW_UNSUBSCRIBE_TOKEN,
  TEMPLATE_NAME_MAX,
  blocksText,
  broadcastEmailHtml,
  broadcastListState,
  emailBlockSpec,
  progressLine,
  confirmPhrase,
  unsubscribeUrl,
  audienceReachLine,
  smsSendingNote,
  channelLabel,
  offeredChannels,
  violationSentence,
  DELIVERY_NOT_CLAIMED,
} from "../broadcasts.js";

type Block = { kind: string; text?: string; label?: string; href?: string; src?: string; alt?: string };
type Template = { id: string; name: string; subject: string; preheader: string; blocks: Block[] };

/**
 * **ONE BLOCK'S CONTROLS. HOOKLESS ON PURPOSE, SO A GATE CALLS IT** (R7/R12's entry requirement) —
 * every field it draws is read off `EMAIL_BLOCK_KINDS`, so a sixth kind added to that roster grows
 * its controls by EXISTING rather than by somebody remembering this file.
 */
export function BlockCard({
  block, index, count, onChange, onMove, onRemove,
}: {
  block: Block; index: number; count: number;
  onChange: (next: Block) => void; onMove: (by: number) => void; onRemove: () => void;
}) {
  const spec = emailBlockSpec(block.kind);
  const set = (field: string, value: string) => onChange({ ...block, [field]: value });
  return (
    <div className="owner-broadcasts__blk">
      <div className="owner-broadcasts__blk-head">
        <span className="owner-broadcasts__blk-kind">{spec ? spec.label : block.kind}</span>
        <span className="owner-broadcasts__blk-tools">
          <button type="button" onClick={() => onMove(-1)} disabled={index === 0} aria-label="Move up">Up</button>
          <button type="button" onClick={() => onMove(1)} disabled={index >= count - 1} aria-label="Move down">Down</button>
          <button type="button" onClick={onRemove} aria-label="Remove block">Remove</button>
        </span>
      </div>
      {(spec?.fields || []).map((f) => (
        <Fragment key={f}>
          <label className="owner-broadcasts__field">
            <span>{FIELD_LABELS[f] || f}</span>
            {f === "text" && block.kind === "text" ? (
              <textarea value={block.text || ""} rows={4} onChange={(e) => set(f, e.target.value)} />
            ) : f === "href" ? (
              // >>> **HER PICKER. THE FREE-TEXT FIELD STAYS BESIDE IT AND IS NEVER REPLACED BY IT.**
              // Her clause ends *"unless she wants to"*, so the picker WRITES INTO the same field she
              // can always type in: choosing an entry fills it, typing over it un-selects the entry,
              // and there is exactly one value — a picker holding its own state beside a text box is
              // two answers to where the button points (R28/R29), which on this control is a
              // marketing send whose button goes somewhere she did not choose.
              <span className="owner-broadcasts__dest-row">
                <select
                  aria-label="Pick a destination"
                  className="owner-broadcasts__dest"
                  value={destinationForPath(block.href || "")?.path || ""}
                  onChange={(e) => { if (e.target.value) set("href", e.target.value); }}
                >
                  <option value="">Somewhere else…</option>
                  {DESTINATION_GROUPS.map((g) => (
                    <optgroup key={g} label={g}>
                      {BROADCAST_DESTINATIONS.filter((d) => d.group === g).map((d) => (
                        <option key={d.path} value={d.path}>{d.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <input value={block.href || ""} onChange={(e) => set(f, e.target.value)} placeholder={FIELD_HINTS[f] || ""} />
              </span>
            ) : (
              <input value={(block as Record<string, string>)[f] || ""} onChange={(e) => set(f, e.target.value)} placeholder={FIELD_HINTS[f] || ""} />
            )}
          </label>
          {/* **HER FLAG, AND IT IS A MARK RATHER THAN A REFUSAL.** *External URLs allowed but flagged
              visually as leaving the site* — so this says what is true and blocks nothing. It is
              drawn from `isExternalDestination`, the same law the ledger records the flag with, so
              what she is shown and what her audit trail says can never disagree. */}
          {f === "href" && isExternalDestination(block.href || "") ? (
            <p className="owner-broadcasts__leaves" data-external="true">This link leaves 12thhouse.ai.</p>
          ) : null}
        </Fragment>
      ))}
      {/* A divider has no fields, so it says what it is rather than drawing an empty card. */}
      {spec && spec.fields.length === 0 ? <p className="owner-broadcasts__quiet">A gold rule with the house mark.</p> : null}
    </div>
  );
}

// Her words for the fields, and the two hints that stop a URL going in the wrong shape. A relative
// path is resolved to the site; anything that is not a web address is refused at the door rather
// than sent as a dead button (`emailAbsoluteUrl`).
const FIELD_LABELS: Record<string, string> = {
  text: "Words", label: "Button words", href: "Where it goes", src: "Picture address", alt: "Describe the picture",
};
const FIELD_HINTS: Record<string, string> = {
  href: "/living-room or https://…", src: "/email-header.jpg or https://…", alt: "Left empty, it is treated as decoration",
};

type Counts = { people: number; pushReach: number; emailReach: number; smsReach?: number } | null;
type Audience = { key: string; label: string; note: string; counts: Counts };
type Rails = Record<string, { armed: boolean; why: string }>;

// A payload older than this round carries no reason — a tab left open across the deploy — and the
// screen falls back to exactly the sentence it said before rather than to a blank line (R18/R65).
const SEND_BLOCK_FALLBACK = "This cannot be sent yet.";

type Preview = {
  ok: boolean;
  audience: string; audienceLabel: string; counts: Counts;
  channels: string[];
  violations: string[];
  push: { title: string; body: string; url: string } | null;
  email: { subject: string; html: string | null; text: string | null } | null;
  mail: { armed: boolean; why: string; ceiling: number };
  capacity: { state: string; may: boolean; sentence: string };
  emailBatches: { total: number; size: number; batches: number } | null;
  confirmPhrase: string;
  maySend: boolean;
  blockReason?: string;
};
type Row = {
  id: string; audience: string; audienceLabel: string; channels: string[];
  title: string; subject: string; state: string;
  sent: number; failed: number; total: number;
  createdAt: string | null;
};

// Her sentence per reason. Plain language, ITEM A's law: no machine key reaches this screen.
const MAIL_OFF: Record<string, string> = {
  "no-credentials": "The mail rail is not switched on, so email cannot be sent yet.",
  "retired-from-address": "Email is held back because the address it would come from is one we retired. Members would see an address that is no longer ours.",
  "no-declared-ceiling": "Email is held back until we say how many messages this mailbox can send in a day. Without that, a big send could stop half way and nobody would know.",
  default: "Email cannot be sent yet.",
};

const json = async (url: string, init?: RequestInit) => {
  const res = await fetch(url, { credentials: "include", ...init });
  try { return await res.json(); } catch { return null; }
};

export default function OwnerBroadcasts() {
  const [audiences, setAudiences] = useState<Audience[] | null>(null);
  const [mail, setMail] = useState<{ armed: boolean; why: string; ceiling: number } | null>(null);
  // >>> HER CLAUSE 2 (2026-08-18): EACH CHANNEL GATES ON ITS RAIL'S ARMED STATE. <<< The fact is the
  // SERVER'S — a browser cannot see TWILIO_DAILY_CEILING — so it travels on the payload and `null`
  // is *we have not been told yet*, which is not an unarmed rail (R18).
  const [rails, setRails] = useState<Rails | null>(null);
  const [unsubscribed, setUnsubscribed] = useState<{ count: number; latest: string | null } | null>(null);
  const [audiencesFailed, setAudiencesFailed] = useState(false);

  const [audience, setAudience] = useState("");
  const [channels, setChannels] = useState<string[]>(["push"]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [url, setUrl] = useState("/");
  const [subject, setSubject] = useState("");
  // **`emailBody` SURVIVES AS THE FALLBACK AND IS NO LONGER TYPED INTO.** Her order replaces the one
  // text box with the stack; every broadcast in the ledger from before today still carries a body
  // and `emailContent` still renders it, so nothing already sent stops resolving (R68).
  const [emailBody] = useState("");
  const [preheader, setPreheader] = useState("");
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [templates, setTemplates] = useState<Template[] | null>(null);
  const [templatesFailed, setTemplatesFailed] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateNote, setTemplateNote] = useState("");

  const [preview, setPreview] = useState<Preview | null>(null);
  const [confirm, setConfirm] = useState("");
  const [notice, setNotice] = useState("");
  const [live, setLive] = useState<Row | null>(null);

  const [rows, setRows] = useState<Row[] | undefined>(undefined);
  const [historyFailed, setHistoryFailed] = useState(false);

  // **A `useRef`, NOT STATE (D-5's law, one room over).** A `useState` guard reads the value from the
  // render that BOUND the handler, so two taps inside one frame both send — and on this control that
  // is two broadcasts to the whole house. The state below is what the button WEARS, so the refusal
  // is visible rather than swallowed.
  const inFlight = useRef(false);
  const [busy, setBusy] = useState(false);

  const loadAudiences = async () => {
    const r = await json("/api/broadcast/audiences");
    if (!r?.ok) { setAudiencesFailed(true); return; }
    setAudiencesFailed(false);
    setAudiences(r.audiences || []);
    setMail(r.mail || null);
    setRails((r.rails as Rails) || null);
    setUnsubscribed(r.unsubscribed && typeof r.unsubscribed.count === "number" ? r.unsubscribed : null);
  };
  const loadHistory = async () => {
    const r = await json("/api/broadcast/history");
    if (!r?.ok) { setHistoryFailed(true); setRows([]); return; }
    setHistoryFailed(false);
    setRows(r.broadcasts || []);
  };

  useEffect(() => { loadAudiences(); loadHistory(); loadTemplates(); }, []);

  // The progress poll. It runs only while something is in flight and stops itself the moment the
  // send is finished — a dashboard that polls forever is a dashboard that is always slightly warm.
  useEffect(() => {
    if (!live || live.state === "done" || live.state === "failed") return;
    const t = setInterval(async () => {
      const r = await json(`/api/broadcast/${encodeURIComponent(live.id)}`);
      if (r?.ok && r.broadcast) {
        setLive(r.broadcast);
        if (r.broadcast.state === "done" || r.broadcast.state === "failed") loadHistory();
      }
    }, 2000);
    return () => clearInterval(t);
  }, [live?.id, live?.state]);

  const draft = { audience, channels, title, body, url, subject, emailBody, blocks, preheader };

  // ---- her block composer -------------------------------------------------
  // Every edit clears the server preview, exactly as every other field on this screen does: a
  // preview she read before a change is not a preview of what would now be sent, and her safety
  // clause is that Send does not exist until a preview has come back (R216's ruling one screen over).
  const bump = () => { setPreview(null); setConfirm(""); };
  const addBlock = (kind: string) => { setBlocks((prev) => [...prev, { kind }]); bump(); };
  const editBlock = (i: number, next: Block) => { setBlocks((prev) => prev.map((b, j) => (j === i ? next : b))); bump(); };
  const removeBlock = (i: number) => { setBlocks((prev) => prev.filter((_, j) => j !== i)); bump(); };
  const moveBlock = (i: number, by: number) => {
    setBlocks((prev) => {
      const to = i + by;
      if (to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      const [held] = next.splice(i, 1);
      next.splice(to, 0, held);
      return next;
    });
    bump();
  };

  // **THE LIVE PREVIEW IS THE SEND'S OWN COMPOSER, CALLED HERE.** One declaration, so what she reads
  // while she builds cannot be a second answer to what a member receives (R28/R29). The unsubscribe
  // token is the one thing it cannot have — it is signed per member on the server — so a sample is
  // used and said so beneath it.
  const liveHtml = broadcastEmailHtml({
    subject, body: emailBody, blocks, preheader,
    unsubscribe: unsubscribeUrl(typeof window === "undefined" ? "" : window.location.origin, PREVIEW_UNSUBSCRIBE_TOKEN),
  });
  const wordCount = blocksText(blocks).length;

  const loadTemplates = async () => {
    const r = await json("/api/broadcast/templates");
    if (!r?.ok) { setTemplatesFailed(true); return; }
    setTemplatesFailed(false);
    setTemplates(r.templates || []);
  };
  const saveTemplate = async () => {
    setTemplateNote("");
    const r = await json("/api/broadcast/templates", {
      method: "POST", headers: { "content-type": "application/json" },
      body: JSON.stringify({ name: templateName, subject, preheader, blocks }),
    });
    // R39/R47: the answer is read off the PAYLOAD and never off the status code, so a refusal
    // cannot be reported to her as a save.
    if (!r?.ok) { setTemplateNote(r?.message || "That template could not be saved."); return; }
    setTemplateNote(r.message || "Saved.");
    loadTemplates();
  };
  const loadTemplate = (t: Template) => {
    setSubject(t.subject || "");
    setPreheader(t.preheader || "");
    setBlocks(Array.isArray(t.blocks) ? t.blocks : []);
    setTemplateName(t.name || "");
    bump();
  };
  // R39/R47: the ANSWER is the payload and never the status code, and `DeleteControl` reads the
  // boolean back — so a refusal keeps its own failed state on the control rather than being reported
  // to her as a removal that happened.
  const removeTemplate = async (id: string) => {
    const r = await json(`/api/broadcast/templates/${encodeURIComponent(id)}`, { method: "DELETE" });
    if (!r?.ok) { setTemplateNote(r?.message || "That template could not be removed."); return false; }
    setTemplateNote("");
    await loadTemplates();
    return true;
  };

  const toggleChannel = (c: string) => {
    setPreview(null); setConfirm("");
    setChannels((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  };

  const doPreview = async () => {
    setNotice("");
    const r = await json("/api/broadcast/preview", {
      method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(draft),
    });
    if (!r?.ok) { setNotice(r?.message || "The preview could not be built."); return; }
    setPreview(r as Preview);
    setConfirm("");
  };

  const doSend = async () => {
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true); setNotice("");
    try {
      const r = await json("/api/broadcast/send", {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ ...draft, confirm }),
      });
      if (!r?.ok) {
        setNotice(r?.message || "That did not send.");
        // The audience moved under her, so the preview she is looking at is stale. Re-taken rather
        // than left on screen with a number that is no longer true.
        if (r?.reason === "count-moved") await doPreview();
        return;
      }
      setPreview(null); setConfirm("");
      const fresh = await json(`/api/broadcast/${encodeURIComponent(r.id)}`);
      if (fresh?.ok) setLive(fresh.broadcast);
      loadHistory(); loadAudiences();
    } finally {
      // In a `finally`, because this is the one control here whose stuck state is a permanently dead
      // Send with her whole broadcast still typed into it.
      inFlight.current = false; setBusy(false);
    }
  };

  const listState = broadcastListState({ rows, failed: historyFailed });
  const selected = audiences?.find((a) => a.key === audience) || null;

  return (
    <div className="owner-broadcasts">
      <p className="owner-broadcasts__note">
        A message from the house to the house. Nothing is sent until you have seen it and typed the confirmation.
      </p>

      <section className="owner-broadcasts__block">
        <h3 className="owner-broadcasts__h">Who it goes to</h3>
        {audiencesFailed ? (
          <p className="owner-broadcasts__warn">The member list could not be read, so no counts are shown.</p>
        ) : audiences === null ? (
          <p className="owner-broadcasts__quiet">Counting…</p>
        ) : (
          <div className="owner-broadcasts__audiences">
            {audiences.map((a) => (
              <button
                key={a.key} type="button"
                className={`owner-broadcasts__aud${audience === a.key ? " is-on" : ""}`}
                onClick={() => { setAudience(a.key); setPreview(null); setConfirm(""); }}
              >
                <span className="owner-broadcasts__aud-label">{a.label}</span>
                {/* R18 ON A NUMBER: a count that has not arrived shows nothing rather than a
                    confident zero, which on this screen reads as "nobody is in that audience". */}
                <span className="owner-broadcasts__aud-n">{a.counts ? a.counts.people : "—"}</span>
                <span className="owner-broadcasts__aud-note">{a.note}</span>
              </button>
            ))}
          </div>
        )}
        {selected?.counts ? (
          <>
            {/* P4: the sentence is COMPOSED by the law rather than assembled here, so a reach that
                did not arrive is omitted instead of drawn as a confident zero (R18/R65). */}
            <p className="owner-broadcasts__reach">{audienceReachLine(selected.counts)}</p>
            {/* And the note draws exactly while there is no SMS sender, derived from the channel
                roster — so it goes by itself the day C9 puts `sms` on it (R31). */}
            {smsSendingNote(rails) ? <p className="owner-broadcasts__quiet">{smsSendingNote(rails)}</p> : null}
          </>
        ) : null}
        {unsubscribed !== null ? (
          <p className="owner-broadcasts__quiet">{unsubscribed.count} {unsubscribed.count === 1 ? "member has" : "members have"} unsubscribed from announcements.</p>
        ) : null}
      </section>

      <section className="owner-broadcasts__block">
        <h3 className="owner-broadcasts__h">How it goes</h3>
        <div className="owner-broadcasts__channels">
          {/* >>> HER DEFECT 2 (2026-08-18): THIS RENDERED {c === "push" ? "Push" : "Email"} — a
              two-way ternary over a roster C9 made THREE — so `sms` came out labelled *Email* and
              she was looking at the same word twice. The label is a DECLARATION per channel now,
              held as a biconditional against the roster, so a fourth joins by going red rather than
              inheriting somebody else's word. <<<

              AND HER CLAUSE 2: only a channel whose RAIL IS ARMED is offered. A tickbox for a rail
              that cannot send is A2's dead box on the one control that costs money — she ticks it,
              types the confirmation, and the loop writes a ledger row for a broadcast that reached
              nobody (R141). */}
          {offeredChannels(rails).map((c) => (
            <label key={c} className="owner-broadcasts__channel" data-channel={c}>
              <input type="checkbox" checked={channels.includes(c)} onChange={() => toggleChannel(c)} />
              <span>{channelLabel(c)}</span>
            </label>
          ))}
        </div>
        {/* The mail rail's state is said plainly rather than shown as a control that quietly fails. */}
        {/* **WHY, NOT JUST OFF (R18).** Three different facts stop the mail rail and only one of them
            is "nobody set it up": the From address may be one W1-15 retired (which would put a dead
            address in front of every member at once), or the mailbox's daily ceiling may be
            undeclared (which fails closed rather than guessing). A single "not switched on" sends
            her looking in the wrong place for two of the three. */}
        {mail && !mail.armed ? (
          <p className="owner-broadcasts__warn">{MAIL_OFF[mail.why] || MAIL_OFF.default}</p>
        ) : null}
      </section>

      {channels.includes("push") ? (
        <section className="owner-broadcasts__block">
          <h3 className="owner-broadcasts__h">The push</h3>
          <label className="owner-broadcasts__field">
            <span>Title</span>
            <input value={title} onChange={(e) => { setTitle(e.target.value); setPreview(null); }} />
          </label>
          <label className="owner-broadcasts__field">
            <span>Body</span>
            <textarea
              value={body} rows={3} maxLength={BROADCAST_PUSH_BODY_MAX}
              onChange={(e) => { setBody(e.target.value); setPreview(null); }}
            />
          </label>
          <label className="owner-broadcasts__field">
            <span>Opens</span>
            <input value={url} onChange={(e) => { setUrl(e.target.value); setPreview(null); }} placeholder="/living-room" />
          </label>
        </section>
      ) : null}

      {channels.includes("email") ? (
        <section className="owner-broadcasts__block">
          <h3 className="owner-broadcasts__h">The email</h3>
          <label className="owner-broadcasts__field">
            <span>Subject</span>
            <input
              value={subject} maxLength={BROADCAST_SUBJECT_MAX}
              onChange={(e) => { setSubject(e.target.value); setPreview(null); }}
            />
          </label>
          {/* The grey line beside the subject in their list. Its own field because it is its own
              fact (R18): a subject written to double as a preview is a subject written for two jobs,
              and left empty the mail client makes one up out of the ALT text of her banner. */}
          <label className="owner-broadcasts__field">
            <span>Preview line</span>
            <input
              value={preheader} maxLength={EMAIL_PREHEADER_MAX}
              onChange={(e) => { setPreheader(e.target.value); setPreview(null); }}
              placeholder="The line they see beside the subject"
            />
          </label>

          {/* >>> HER BLOCK COMPOSER. She stacks blocks in order; the shell dresses them. <<< */}
          <div className="owner-broadcasts__compose">
            <div className="owner-broadcasts__stack">
              {blocks.length === 0 ? (
                <p className="owner-broadcasts__quiet">Nothing in the email yet. Add a block to begin.</p>
              ) : null}
              {blocks.map((b, i) => (
                <BlockCard
                  key={i}
                  block={b}
                  index={i}
                  count={blocks.length}
                  onChange={(next) => editBlock(i, next)}
                  onMove={(by) => moveBlock(i, by)}
                  onRemove={() => removeBlock(i)}
                />
              ))}
              <div className="owner-broadcasts__adds">
                {EMAIL_BLOCK_KINDS.map((k) => (
                  <button
                    key={k.kind} type="button" className="owner-broadcasts__add"
                    onClick={() => addBlock(k.kind)}
                    disabled={blocks.length >= EMAIL_BLOCKS_MAX}
                  >
                    + {k.label}
                  </button>
                ))}
              </div>
              <p className="owner-broadcasts__quiet">
                {wordCount} of {BROADCAST_BODY_MAX} characters. Every announcement email carries an unsubscribe link. It is added for you.
              </p>
            </div>

            {/* **THE LIVE PREVIEW IS THE SAME COMPOSER THE SERVER SENDS THROUGH, CALLED HERE.** Not a
                second renderer that agrees with it today (R28/R29) — `broadcastEmailHtml` is pure and
                imports the E3 shell, so what she watches while she builds is the mail itself. What it
                cannot carry is a real unsubscribe token, which is signed on the server and is one per
                member; the sample is declared under it rather than passed off as hers. */}
            <div className="owner-broadcasts__live">
              <p className="owner-broadcasts__card-kind">Live preview</p>
              {liveHtml ? (
                <iframe className="owner-broadcasts__mail" title="Live email preview" srcDoc={liveHtml} />
              ) : (
                <p className="owner-broadcasts__quiet">Add some words and the email appears here.</p>
              )}
              <p className="owner-broadcasts__quiet">{PREVIEW_LINK_NOTE}</p>
            </div>
          </div>

          {/* HER SAVE AS TEMPLATE CONTROL — a campaign she likes, reusable with the words swapped. */}
          <div className="owner-broadcasts__templates">
            <label className="owner-broadcasts__field">
              <span>Save this as a template</span>
              <input
                value={templateName} maxLength={TEMPLATE_NAME_MAX}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Name it, so you can find it again"
              />
            </label>
            <button type="button" className="owner-broadcasts__preview-btn" onClick={saveTemplate} disabled={!templateName.trim() || !blocks.length}>
              Save as template
            </button>
            {templateNote ? <p className="owner-broadcasts__quiet">{templateNote}</p> : null}
            {templates && templates.length ? (
              <ul className="owner-broadcasts__template-list">
                {templates.map((t) => (
                  <li key={t.id}>
                    <button type="button" className="owner-broadcasts__template" onClick={() => loadTemplate(t)}>{t.name}</button>
                    {/* R35: THE ONE WAY THIS HOUSE ASKS BEFORE IT DESTROYS SOMETHING OF HERS. A
                        template is a campaign she built block by block and named, and a bare tap
                        that removes it is exactly what that control exists to refuse. */}
                    <DeleteControl kind="template" what={t.name} onDelete={() => removeTemplate(t.id)} />
                  </li>
                ))}
              </ul>
            ) : null}
            {templatesFailed ? <p className="owner-broadcasts__warn">Your templates could not be read.</p> : null}
          </div>
        </section>
      ) : null}

      <section className="owner-broadcasts__block">
        <button type="button" className="owner-broadcasts__preview-btn" onClick={doPreview} disabled={!audience || !channels.length}>
          Preview
        </button>
        {notice ? <p className="owner-broadcasts__warn">{notice}</p> : null}
      </section>

      {preview ? (
        <section className="owner-broadcasts__block owner-broadcasts__preview">
          <h3 className="owner-broadcasts__h">What they will see</h3>
          <p className="owner-broadcasts__count">
            {preview.counts?.people ?? 0} {(preview.counts?.people ?? 0) === 1 ? "person" : "people"} — {preview.audienceLabel}
          </p>
          {preview.violations.length ? (
            <ul className="owner-broadcasts__violations">
              {/* FOUND IN HER OWN CAPTURE: this rendered the RAW LABEL, so her screen said
                  channel-not-for-audience:push. Every one of those keys is refused by her own
                  plain-language law (ITEM A), and Broadcasts is one of her six sections. */}
              {preview.violations.map((v) => <li key={v}>{violationSentence(v)}</li>)}
            </ul>
          ) : null}
          {preview.push ? (
            <div className="owner-broadcasts__card">
              <p className="owner-broadcasts__card-kind">On a phone</p>
              <p className="owner-broadcasts__card-title">{preview.push.title}</p>
              <p className="owner-broadcasts__card-body">{preview.push.body}</p>
              <p className="owner-broadcasts__quiet">Opens {preview.push.url}</p>
            </div>
          ) : null}
          {preview.email ? (
            <div className="owner-broadcasts__card">
              <p className="owner-broadcasts__card-kind">In their mail</p>
              <p className="owner-broadcasts__card-title">{preview.email.subject}</p>
              <iframe className="owner-broadcasts__mail" title="Email preview" srcDoc={preview.email.html || ""} />
              <p className="owner-broadcasts__quiet">{preview.capacity.sentence}</p>
              {/* **HOW LONG THE MAIL HALF WILL TAKE, BECAUSE A DETACHED SEND WITH NO ESTIMATE READS
                  AS A STUCK ONE.** The email rail is paced between batches on purpose (a mailbox is
                  not a bulk rail), so a 937-person send is minutes rather than seconds — and a
                  progress line that has not moved for two minutes is exactly what a person reads as
                  broken and taps again. */}
              {preview.emailBatches && preview.emailBatches.batches > 1 ? (
                <p className="owner-broadcasts__quiet">
                  It goes out in {preview.emailBatches.batches} batches of {preview.emailBatches.size}, so it will take a few minutes.
                </p>
              ) : null}
            </div>
          ) : null}

          {preview.maySend ? (
            <div className="owner-broadcasts__confirm">
              <p>Type <strong>{preview.confirmPhrase}</strong> to send.</p>
              <input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={preview.confirmPhrase} />
              <button
                type="button" className="owner-broadcasts__send"
                onClick={doSend}
                disabled={busy || confirm.trim() !== preview.confirmPhrase}
              >
                {busy ? "Sending…" : "Send"}
              </button>
            </div>
          ) : (
            <p className="owner-broadcasts__warn">{preview.blockReason || SEND_BLOCK_FALLBACK}</p>
          )}
        </section>
      ) : null}

      {live ? (
        <section className="owner-broadcasts__block">
          <h3 className="owner-broadcasts__h">This send</h3>
          <p className="owner-broadcasts__progress">{progressLine(live)}</p>
          <p className="owner-broadcasts__quiet">{DELIVERY_NOT_CLAIMED}</p>
        </section>
      ) : null}

      <section className="owner-broadcasts__block">
        <h3 className="owner-broadcasts__h">Everything you have sent</h3>
        {/* FOUR STATES, NEVER TWO (R18/R64): a read that has not answered HOLDS STILL rather than
            telling her she has never sent anything. */}
        {listState === "waiting" ? <p className="owner-broadcasts__quiet">Reading…</p> : null}
        {listState === "failed" ? <p className="owner-broadcasts__warn">The history could not be read.</p> : null}
        {listState === "empty" ? <p className="owner-broadcasts__quiet">Nothing has been sent yet.</p> : null}
        {listState === "list" ? (
          <ul className="owner-broadcasts__history">
            {(rows || []).map((r) => (
              <li key={r.id} className="owner-broadcasts__row">
                <span className="owner-broadcasts__row-title">{r.title || r.subject || "(no title)"}</span>
                <span className="owner-broadcasts__row-aud">{r.audienceLabel || r.audience}</span>
                <span className="owner-broadcasts__row-chan">{r.channels.join(" + ")}</span>
                <span className="owner-broadcasts__row-progress">{progressLine(r)}</span>
              </li>
            ))}
          </ul>
        ) : null}
      </section>
    </div>
  );
}

export { BROADCAST_AUDIENCES, confirmPhrase };
